import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Alert,
  LinearProgress,
  Collapse,
  Divider,
  Box,
} from "@mui/material";
import {
  PlayArrow as PlayIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { odooService } from "../../services/odooService";

interface OdooMigrationFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Dialogo de confirmacion para importar la base de datos Odoo 16
 *
 * REGLAS: Solo <Stack>, size="small", desktop-first, max 500 lineas
 */
export function OdooMigrationForm({
  open,
  onClose,
  onSuccess,
}: OdooMigrationFormProps): React.ReactElement {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [config] = useState(() => odooService.getOptimizedConfig());
  const environmentInfo = odooService.getEnvironmentInfo();

  const handleSubmit = async () => {
    try {
      setUploading(true);
      setProgress(0);
      setProgressLabel("Enviando archivo al servidor...");
      setError(null);

      // Progress simulation aligned with real stages
      const stages = [
        { at: 15, label: "Subiendo archivo SQL..." },
        { at: 40, label: "Procesando dump en PostgreSQL..." },
        { at: 70, label: "Creando tablas e indices..." },
        { at: 90, label: "Verificando integridad..." },
      ];
      let stageIdx = 0;

      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          const next = Math.min(prev + 2, 95);
          const stage = stages[stageIdx];
          if (stage && next >= stage.at) {
            setProgressLabel(stage.label);
            stageIdx++;
          }
          return next;
        });
      }, 1500);

      const response = await odooService.executeDatabaseMigration(config);

      clearInterval(progressInterval);
      setProgress(100);
      setProgressLabel("Importacion completada");

      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1500);
    } catch (error: any) {
      let errorMessage = "Error ejecutando la importacion.";

      if (error?.response?.data?.message) {
        if (Array.isArray(error.response.data.message)) {
          errorMessage = error.response.data.message.join(". ");
        } else {
          errorMessage = String(error.response.data.message);
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (!uploading) {
      setProgress(0);
      setProgressLabel("");
      setError(null);
      setShowAdvanced(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={600}>
          Importar Base de Datos Odoo 16
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2.5}>
          {/* Warning about destructive action */}
          {!uploading && !error && progress < 100 && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ py: 0.5 }}>
              <Typography variant="body2">
                Esto <strong>reemplazara</strong> todos los datos actuales en la
                base de datos <code>odoo_16</code>.
              </Typography>
            </Alert>
          )}

          {/* What will happen */}
          {!uploading && progress < 100 && (
            <Stack spacing={1}>
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={500}
              >
                Se ejecutaran los siguientes pasos:
              </Typography>
              <Stack spacing={0.5} sx={{ pl: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  1. Limpiar la base de datos <code>odoo_16</code> existente
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  2. Importar el archivo <strong>villapps.sql</strong> (~5.6 GB)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  3. Verificar que las tablas se crearon correctamente
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.disabled">
                Duracion estimada: 3-10 minutos dependiendo del equipo.
              </Typography>
            </Stack>
          )}

          {/* Progress */}
          {uploading && (
            <Stack spacing={1.5}>
              <LinearProgress
                variant={progress < 10 ? "indeterminate" : "determinate"}
                value={progress}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="body2" fontWeight={500}>
                  {progressLabel}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(progress)}%
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.disabled">
                No cierres esta ventana hasta que termine.
              </Typography>
            </Stack>
          )}

          {/* Completed */}
          {progress === 100 && !uploading && (
            <Alert severity="success" sx={{ py: 0.5 }}>
              Importacion completada exitosamente.
            </Alert>
          )}

          {/* Error */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{ py: 0.5 }}
            >
              <Typography variant="body2">{error}</Typography>
            </Alert>
          )}

          {/* Advanced config - collapsed by default */}
          {!uploading && progress < 100 && (
            <>
              <Divider />
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => setShowAdvanced(!showAdvanced)}
                sx={{ cursor: "pointer", py: 0.5 }}
              >
                <Typography variant="caption" color="text.disabled">
                  Configuracion avanzada
                </Typography>
                {showAdvanced ? (
                  <ExpandLessIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                ) : (
                  <ExpandMoreIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                )}
              </Stack>
              <Collapse in={showAdvanced}>
                <Stack direction="row" spacing={3} sx={{ pb: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.disabled">
                      Workers
                    </Typography>
                    <Typography variant="body2">
                      {config.parallelWorkers}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.disabled">
                      Batch
                    </Typography>
                    <Typography variant="body2">{config.batchSize}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.disabled">
                      Timeout
                    </Typography>
                    <Typography variant="body2">
                      {config.timeoutSeconds}s
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.disabled">
                      Compresion
                    </Typography>
                    <Typography variant="body2">
                      {config.useCompression ? "Si" : "No"}
                    </Typography>
                  </Box>
                </Stack>
              </Collapse>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={uploading} size="small">
          {progress === 100 ? "Cerrar" : "Cancelar"}
        </Button>
        {progress < 100 && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={uploading}
            size="small"
            startIcon={<PlayIcon />}
            color="primary"
          >
            {uploading ? "Importando..." : "Confirmar e Importar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
